import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ControlsOf, strictEmailValidator } from '../../shared/forms/forms-common';
import { IRegisterCommand, RegisterCommand, TokensClient } from '../../shared/model/fss.api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RegisterStep1 } from './register-step-1/register-step-1';
import { RegisterStep2 } from './register-step-2/register-step-2';
import { RegisterStep3 } from './register-step-3/register-step-3';
import { StepperModule } from 'primeng/stepper';
import { Badge } from 'primeng/badge';
import { StepSuccess } from '../../shared/icons/step-success/step-success';
import { LocationService } from '../../shared/services/location.service';
import { AlertService } from '../../shared/services/alert.service';
import { firstValueFrom } from 'rxjs';
import { Dialog } from 'primeng/dialog';
import { CheckCircle } from '../../shared/icons/check-circle/check-circle';
import { Building05 } from '../../shared/icons/building-05/building-05';
import { UserCircle } from '../../shared/icons/user-circle/user-circle';
import { Chip } from 'primeng/chip';
import { ButtonComponent, ButtonSeverity, ButtonSize } from '../../shared/ui/button/button';
import { LoaderService } from '../../shared/components/loader/loader.service';

@Component({
  standalone: true,
  selector: 'app-register',
  imports: [
    RegisterStep1,
    RegisterStep2,
    RegisterStep3,
    StepperModule,
    Badge,
    StepSuccess,
    Dialog,
    CheckCircle,
    Chip,
    Building05,
    UserCircle,
    ButtonComponent,
  ],
  templateUrl: './register.html',
  styleUrl: './register.scss',
})
export class Register implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _fb = inject(FormBuilder);
  private readonly _alert = inject(AlertService);
  private readonly _client = inject(TokensClient);
  private readonly _loader = inject(LoaderService);
  public readonly location = inject(LocationService);

  badgeStyle = {
    border: {
      radius: '8px',
    },
    secondary: {
      color: '#414651',
      background: '#FFFFFF',
    },
  };

  inactiveBadgeStyle = {
    border: {
      radius: '8px',
    },
    secondary: {
      color: '#7E838F',
      background: '#FFFFFF',
    },
  };

  currentYear = new Date().getFullYear();
  activeStep = 1;

  form!: FormGroup<ControlsOf<IRegisterCommand>>;

  isConfirmationDialogVisible = signal(false);

  readonly steps = [
    { value: 1, title: 'Tip naloga', subtitle: 'Izaberite vrstu naloga' },
    { value: 2, title: 'Podaci o nalogu', subtitle: 'Unesite potrebne informacije' },
    { value: 3, title: 'Pristup nalogu', subtitle: 'Kreirajte podatke za prijavu' },
  ];

  private readonly _stepFieldsMap: Record<number, (keyof IRegisterCommand)[]> = {
    1: ['isLegalEntity'],
    2: ['firstName', 'lastName', 'address', 'city', 'postalCode', 'companyName', 'pib', 'mb'],
    3: ['email', 'password', 'confirmPassword'],
  };

  ngOnInit(): void {
    this.form = this._fb.group<ControlsOf<IRegisterCommand>>({
      firstName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(120)],
      }),
      lastName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(120)],
      }),
      address: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(120)],
      }),
      city: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(60)],
      }),
      postalCode: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(120)],
      }),
      email: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, strictEmailValidator, Validators.maxLength(64)],
      }),
      password: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(6)],
      }),
      confirmPassword: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      isLegalEntity: new FormControl(true, { nonNullable: true }),
      companyName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(120)],
      }),
      pib: new FormControl('', { nonNullable: true }),
      mb: new FormControl('', { nonNullable: true }),
      phoneNumber: new FormControl('', { nonNullable: true }),
    });

    this._toggleCompanyValidators(this.form.controls.isLegalEntity!.value!);

    this.form.controls
      .isLegalEntity!.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((isLegal) => this._toggleCompanyValidators(isLegal!));
  }

  isStepValid(step: number): boolean {
    if (!this.form) return false;
    const fields = this._stepFieldsMap[step];
    if (!fields) return false;
    return fields.every((field) => this.form.controls[field]!.valid);
  }

  next(): void {
    if (!this.isStepValid(this.activeStep)) return;
    this.activeStep++;
    this._scrollToTop();
  }

  back(): void {
    this.activeStep--;
    this._scrollToTop();
  }

  private _scrollToTop(): void {
    window.scrollTo({ top: 0 });
    document.querySelector('.register-scroll')?.scrollTo({ top: 0 });
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      // Object.entries(this.form.controls).forEach(([key, control]) => {
      //   if (control.invalid) {
      //     console.log(key, control.errors);
      //   }
      // });
      return;
    }

    let req = this.form.value as RegisterCommand;
    if (req.password !== req.confirmPassword) {
      this._alert.addWarnMsg('Lozinke se ne podudaraju');
      return;
    }

    this._loader.setLoadingText = 'Registracija je u toku...';
    this._loader.setShowLoader = true;
    this._register(req).then(() => {
      this.isConfirmationDialogVisible.set(true);
    })
  }

  buyPackage(): void {
    this.isConfirmationDialogVisible.set(false);
    this.location.routeToLogin('/company/packages/offer');
  }

  private _validateStep(step: number): boolean {
    if (!this.form) return false;
    const fields = this._stepFieldsMap[step];

    for (const field of fields) {
      const control = this.form.controls[field];
      control!.markAsTouched();
      control!.updateValueAndValidity();
    }

    return fields.every((field) => this.form.controls[field]!.valid);
  }

  private _toggleCompanyValidators(isLegal: boolean): void {
    const { companyName, pib, mb } = this.form.controls;

    if (isLegal) {
      companyName.setValidators([Validators.required, Validators.maxLength(120)]);
      pib.setValidators([Validators.required, Validators.minLength(9), Validators.maxLength(9)]);
      mb.setValidators([Validators.required, Validators.minLength(8), Validators.maxLength(8)]);
    } else {
      companyName.setValidators([Validators.maxLength(120)]);
      pib.clearValidators();
      mb.clearValidators();
    }

    companyName.updateValueAndValidity();
    pib.updateValueAndValidity();
    mb.updateValueAndValidity();
  }

  private async _register(req: RegisterCommand) {
    return await firstValueFrom(this._client.register(req));
  }

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
}
